import React, { useRef, useState } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toast } from "react-toastify";
// import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import AppBar from "@mui/material/AppBar";
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar, Table } from "../components";
import {
    deleteVideo,
    getVideos,
} from "../../../redux/actions/apiActions";
import setGlobalLoader from "../../../redux/actions/loaderActions";
import exportToExcel from "../../../utils/exportToExcel";

const TableComponent = ({ columns, data, title, isRemoteData, tableRef }) => {
    const theme = useTheme();

    return (
        <Box p={4}>
            <Box
                width={1}
                height={1}
                // minHeight={800}
                borderRadius={2}
                border={`2px solid ${theme.palette.divider}`}
                sx={{
                    borderStyle: "dashed",
                }}
            >
                <Table
                    ref={tableRef}
                    columns={columns}
                    data={data}
                    export={false}
                    // search
                    selection={false}
                    toolbar
                    title={title}
                    isRemoteData={isRemoteData}
                />
            </Box>
        </Box>
    );
};

const Videos = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tableRef = useRef();
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true,
    });

    const [openSidebar, setOpenSidebar] = useState(false);

    const handleSidebarOpen = () => {
        setOpenSidebar(true);
    };

    const handleSidebarClose = () => {
        setOpenSidebar(false);
    };

    const open = isMd ? false : openSidebar;

    const handleOnDelete = async (rowData) => {
        let text = `Are you sure you want to delete ${rowData.title}`;
        if (window.confirm(text) === true) {
            try {
                dispatch(setGlobalLoader(true));
                await deleteVideo(rowData._id);
                tableRef.current.onQueryChange();
                toast.success("Video deleted successfully");
                dispatch(setGlobalLoader(false));
            } catch (error) {
                dispatch(setGlobalLoader(false));
                console.log("Delete Video error", error);
                toast.error(
                    error.response.data.msg ??
                    "some error occured while deleting Vedio"
                );
            }
        } else {
            toast.success("Request Cancelled");
        }
    };

    const headers = ["Sr no.", "Title", "Category", "link", "Created At"];
    const columns = [
        { title: "Sr no.", field: "index", filtering: false },
        { title: "Title", field: "title", filtering: false },
        { title: "Category", field: "videoCategoryName", filtering: false },
        { title: "Video", filtering: false,
            render: (rowData) => (
                <div className="tableActions ">
                    <Tooltip title="View">
                        <a href={`https://www.youtube.com/watch?v=${rowData.youtubeVideoId}`} target="_blank" rel="noopener noreferrer">
                            View
                        </a>
                    </Tooltip>
                </div>
            )
        },
        { title: "Created At", field: "createdAt", filtering: false },
        {
            title: "Actions",
            filtering: false,
            render: (rowData) => (
                <div className="tableActions ">
                    <Tooltip title="Delete">
                        <DeleteIcon onClick={() => handleOnDelete(rowData)} />
                    </Tooltip>
                </div>
            ),
        },
    ];

    // Fetch data with server-side pagination
    const fetchVideos = async (query) => {
        return new Promise(async (resolve) => {
            try {
                dispatch(setGlobalLoader(true));

                // Build query string for API
                const page = query.page + 1; // MaterialTable uses 0-based index, API uses 1-based
                const limit = query.pageSize;
                const sort = query.orderBy ? { [query.orderBy.field]: query.orderDirection } : {};

                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    limit: limit.toString(),
                });

                // Add sort if exists
                if (Object.keys(sort).length > 0) {
                    queryParams.append('sort', JSON.stringify(sort));
                }

                const vidRes = await getVideos(`?${queryParams.toString()}`);
                const apiData = vidRes?.data?.data;

                const data = apiData?.docs?.map((d, i) => ({
                    ...d,
                    index: (page - 1) * limit + i + 1,
                    videoCategoryName: d?.videoCategoryId?.title ?? "",
                    createdAt: d?.createdAt ? format(new Date(d.createdAt), "MMM dd, yyyy") : "-",
                }));

                dispatch(setGlobalLoader(false));

                resolve({
                    data: data,
                    page: query.page,
                    totalCount: apiData?.total || 0,
                });
            } catch (error) {
                dispatch(setGlobalLoader(false));
                console.log("get Videos error", error);
                toast.error(error?.response?.data?.msg || "Error fetching videos");
                resolve({
                    data: [],
                    page: 0,
                    totalCount: 0,
                });
            }
        });
    };

    return (
        <Box>
            <AppBar
                position={"fixed"}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={handleSidebarClose}
                open={open}
                variant={isMd ? "permanent" : "temporary"}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <Box
                    display="flex"
                    flex="1 1 auto"
                    overflow="hidden"
                    paddingLeft={{ md: "256px" }}
                >
                    <Box display="flex" flex="1 1 auto" overflow="hidden">
                        <Box flex="1 1 auto" height="100%" overflow="auto">
                            <Box
                                marginTop={3}
                                mx={{ xs: 3, md: 4 }}
                                display={"flex"}
                                justifyContent={"space-between"}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="button"
                                    size="medium"
                                    onClick={() => {
                                        navigate("/video/add");
                                    }}
                                >
                                    Add Video
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    component="button"
                                    size="medium"
                                    onClick={async () => {
                                        try {
                                            dispatch(setGlobalLoader(true));
                                            // Fetch all videos for export
                                            const vidRes = await getVideos("?limit=10000");
                                            const allVideos = vidRes?.data?.data?.docs?.map((d, i) => ({
                                                ...d,
                                                index: i + 1,
                                                videoCategoryName: d?.videoCategoryId?.title ?? "",
                                                createdAt: d?.createdAt ? format(new Date(d.createdAt), "MMM dd, yyyy") : "-",
                                            }));

                                            const rows = allVideos.map((row) => ({
                                                "Sr No. ": row.index,
                                                name: row.title,
                                                category: row.videoCategoryName,
                                                link: `https://www.youtube.com/watch?v=${row.youtubeVideoId}`,
                                                createdAt: row.createdAt,
                                            }));
                                            await exportToExcel(rows, headers, "Videos");
                                            dispatch(setGlobalLoader(false));
                                        } catch (error) {
                                            dispatch(setGlobalLoader(false));
                                            console.log("export error", error);
                                            toast.error("Error exporting videos");
                                        }
                                    }}
                                >
                                    Download
                                </Button>
                            </Box>
                            <TableComponent
                                data={fetchVideos}
                                columns={columns}
                                title="Videos"
                                isRemoteData={true}
                                tableRef={tableRef}
                            />
                            <Divider />
                        </Box>
                    </Box>
                </Box>
            </main>
        </Box>
    );
};

export default Videos;
